/**
 * @file components/settings/LaboratoriesPage.tsx
 * @description 後台「實驗室管理」頁：搜尋 + 啟用篩選 + 表格 CRUD。
 */
import { useMemo, useState } from 'react';
import { Button, Empty, Input, Segmented, Space, Switch, Table, Tag, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { Edit3, Plus, Trash2 } from 'lucide-react';
import type { LaboratoryDto } from '@zhcb/shared';
import { ApiError } from '@/lib/api-client';
import {
  useDeleteLaboratory,
  useLaboratoriesList,
  useUpdateLaboratory,
} from '@/hooks/useLaboratories';
import { LaboratoryFormDrawer } from '@/components/settings/LaboratoryFormDrawer';

type ActiveFilter = 'all' | 'active' | 'inactive';

export function LaboratoriesPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<LaboratoryDto | null>(null);

  const listQ = useLaboratoriesList({
    page,
    pageSize,
    search: search.trim() || undefined,
    // activeFilter='active' 用 server-side activeOnly；'inactive' 在前端過濾
    activeOnly: activeFilter === 'active',
    includeDeleted,
  });
  const update = useUpdateLaboratory();
  const remove = useDeleteLaboratory();

  /** 客戶端二次過濾（僅當 activeFilter='inactive' 時生效） */
  const items = useMemo(() => {
    const all = listQ.data?.items ?? [];
    if (activeFilter === 'inactive') return all.filter((x) => !x.isActive);
    return all;
  }, [listQ.data?.items, activeFilter]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (row: LaboratoryDto) => {
    setEditing(row);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
  };

  const toggleActive = async (row: LaboratoryDto, next: boolean) => {
    try {
      await update.mutateAsync({ id: row.id, input: { isActive: next } });
      message.success(next ? '已啟用' : '已停用');
    } catch (e) {
      message.error((e as Error).message || '操作失敗');
    }
  };

  const onDelete = (row: LaboratoryDto) => {
    modal.confirm({
      title: `軟刪除「${row.name}」？`,
      content:
        '軟刪除後不再出現在管理列表（除非開「顯示已刪除」）與下拉選項。如已被採樣引用會阻止刪除。',
      okText: '刪除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await remove.mutateAsync(row.id);
          message.success('已刪除');
        } catch (e) {
          // 後端 IN_USE 在 details 裡帶 referencedCount；舊版只在 message 帶數字也兼容
          let count: number | null = null;
          if (e instanceof ApiError && e.details && typeof e.details === 'object') {
            const d = e.details as { referencedCount?: number };
            if (typeof d.referencedCount === 'number') count = d.referencedCount;
          }
          if (count == null) {
            const m = (e as Error).message?.match(/(\d+)/);
            count = m ? Number(m[1]) : null;
          }
          modal.warning({
            title: '無法刪除',
            content: count
              ? `該實驗室已被 ${count} 條樣品引用，請改用「停用」。`
              : `該實驗室已被採樣引用，請改用「停用」。原始錯誤：${(e as Error).message ?? ''}`,
          });
        }
      },
    });
  };

  const columns: ColumnsType<LaboratoryDto> = [
    { title: '名稱', dataIndex: 'name', width: 220 },
    {
      title: '認證號',
      dataIndex: 'accreditationNo',
      width: 140,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '聯絡人',
      dataIndex: 'contactPerson',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    { title: '電話', dataIndex: 'phone', width: 140, render: (v: string | null) => v ?? '—' },
    {
      title: '啟用',
      dataIndex: 'isActive',
      width: 80,
      render: (v: boolean, row) => (
        <Switch
          size="small"
          checked={v}
          onChange={(next) => void toggleActive(row, next)}
          disabled={!!row.deletedAt}
        />
      ),
    },
    { title: '排序', dataIndex: 'sortOrder', width: 70 },
    {
      title: '引用次數',
      dataIndex: 'inUseCount',
      width: 90,
      render: (v: number | undefined) =>
        v && v > 0 ? (
          <Tag color="blue">{v}</Tag>
        ) : (
          <Typography.Text type="secondary">0</Typography.Text>
        ),
    },
    {
      title: '狀態',
      width: 90,
      render: (_, row) =>
        row.deletedAt ? <Tag color="default">已刪除</Tag> : <Tag color="green">正常</Tag>,
    },
    {
      title: '操作',
      width: 140,
      render: (_, row) =>
        row.deletedAt ? (
          <Typography.Text type="secondary">已刪除</Typography.Text>
        ) : (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<Edit3 size={12} />}
              onClick={() => openEdit(row)}
            >
              編輯
            </Button>
            <Button
              type="link"
              size="small"
              danger
              icon={<Trash2 size={12} />}
              onClick={() => onDelete(row)}
            >
              刪除
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        實驗室管理
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        維護「採樣 → 送驗實驗室」下拉選項。停用後新增採樣不會出現該選項，但已引用的樣品保留快照。
      </Typography.Paragraph>

      <Space wrap>
        <Input.Search
          placeholder="搜尋名稱 / 認證號"
          allowClear
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={() => setPage(1)}
        />
        <Segmented
          value={activeFilter}
          onChange={(v) => {
            setActiveFilter(v as ActiveFilter);
            setPage(1);
          }}
          options={[
            { label: '全部', value: 'all' },
            { label: '啟用', value: 'active' },
            { label: '停用', value: 'inactive' },
          ]}
        />
        <Space>
          <Switch checked={includeDeleted} onChange={setIncludeDeleted} />
          <Typography.Text>顯示已刪除</Typography.Text>
        </Space>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
          新增實驗室
        </Button>
      </Space>

      {listQ.isLoading ? (
        <Typography.Text>載入中…</Typography.Text>
      ) : items.length ? (
        <Table<LaboratoryDto>
          rowKey="id"
          dataSource={items}
          columns={columns}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total: listQ.data?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      ) : (
        <Empty description="尚無資料" />
      )}

      <LaboratoryFormDrawer open={drawerOpen} editing={editing} onClose={closeDrawer} />
    </Space>
  );
}
